import { toast } from 'sonner';
import { logger } from '@/lib/logger';

export interface QueryError {
  message: string;
  code?: string;
  details?: any;
}

/**
 * Default error handler for React Query
 * Provides user-friendly error messages
 */
export function queryErrorHandler(error: unknown): void {
  let message = 'An unexpected error occurred';
  
  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  } else if (error && typeof error === 'object' && 'message' in error) {
    message = (error as any).message;
  }
  
  // Check for specific error types
  if (message.includes('Failed to fetch')) {
    message = 'Network error. Please check your connection.';
  } else if (message.includes('403') || message.includes('unauthorized')) {
    message = 'You do not have permission to perform this action.';
  } else if (message.includes('404')) {
    message = 'The requested resource was not found.';
  } else if (message.includes('duplicate')) {
    message = 'This item already exists.';
  }
  
  // Show error toast
  toast.error(message);
  
  // Log full error for debugging
  logger.error('Query error:', error);
}

/**
 * Retry logic for queries
 * Retries network errors but not permission errors
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Don't retry more than 3 times
  if (failureCount >= 3) return false;
  
  // Don't retry permission errors
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('403') || message.includes('unauthorized') || message.includes('permission')) {
      return false;
    }
  }
  
  // Retry network errors
  return true;
}